// src/pages/Products/TrafficSafety/SmartWarning.jsx
import React from 'react';
import HeroSection from '../../../components/ProductsDetail/HeroSection';
import ProductShowcaseSection from '../../../components/ProductsDetail/ProductShowcaseSection';
import CTASection from '../../../components/ProductsDetail/CTASection';

// Info blocks
import {
  InfoSection,
  FeatureList,
  TypeBlock,
  UsageSteps,
} from '../../../components/ProductsDetail/InfoBlocks';
import SectionDivider from '../../../components/ProductsDetail/SectionDivider';
import ProductDetailNav from '../../../components/ProductsDetail/ProductDetailNav';


const SmartWarning = () => {
  const compositionGroups = [
    {
      items: [
        { src: '/images/products/traffic-safety/flash-light.webp', label: '스마트 점멸등' },
        { src: '/images/products/traffic-safety/light-stick.webp', label: '스마트 경광봉' },
      ],
    },
  ];

  const features = [
    {
      title: '적·청 LED 교차 점멸로 강력한 경고 효과 제공',
      bullets: [
      {
        label: (
          <>고휘도 LED 적용으로 <strong>주·야간 및 악천후에도 높은 시인성 확보</strong></>
        ),
      },
      {
        label: (
          <>적색·청색 신호의 교차 점멸로 운전자 주목도를 극대화</>
        ),
      },
      {
        label: (
          <>위험 구간 접근 시 즉각적인 감속 및 차로 변경 유도</>
        ),
      },
      ]
    },
    {
      title: '접이식 구조로 편리한 설치 및 이동',
      bullets: [
      {
        label: (
          <>빠르고 간편한 <strong>폴딩(접이식) 설계</strong>로 휴대성 및 보관 효율 향상</>
        ),
      },
      {
        label: (
          <>설치 공간 제약이 적어 다양한 현장에서 즉각 배치 가능</>
        ),
      },
      {
        label: (
          <>차량 적재 및 이동이 용이해 긴급 상황 대응력 강화</>
        ),
      },
      ]
    },
    {
      title: '다양한 위험 상황에 즉각 대응',
      bullets: [
      {
        label: (
          <>고속도로 및 일반도로 공사 구간</>
        ),
      },
      {
        label: (
          <>긴급 복구 및 시설물 점검</>
        ),
      },
      {
        label: (
          <>교통사고 통제 현장</>
        ),
      },
      {
        label: (
          <>돌발 상황 및 장애물 발견 시 긴급 경고 → <strong>각종 현장에서 도로 안전 확보에 최적화된 시스템</strong></>
        ),
      },
      ]
    },
    {
      title: '내구성 강화 및 안정적 운영',
      bullets: [
      {
        label: (
          <>충격·비·먼지에 강한 구조 설계로 장시간 안정적 사용 가능</>
        ),
      },
      {
        label: (
          <>고품질 LED 탑재로 긴 수명과 낮은 유지비 달성</>
        ),
      },
      {
        label: (
          <>현장 환경 변화에도 성능을 유지하는 견고한 내환경성</>
        ),
      },
      ]
    },
    {
      title: '교통 흐름 개선 및 2차 사고 예방 효과',
      bullets: [
      {
        label: (
          <>위험 상황을 조기에 인식시켜 <strong>급정거·추돌 사고 위험 감소</strong></>
        ),
      },
      {
        label: (
          <>정체·혼잡 구간의 효과적 해소</>
        ),
      },
      {
        label: (
          <>작업자 및 운전자의 안전 동시 확보</>
        ),
      },
      ]
    },
  ];

  // ▶ 사용방법(스텝)
  const steps = [
    {
      image: '/images/products/traffic-safety/smart-warning-step1.webp',
      text: '1. 스프링콘을 수축시켜 라바콘의 상부 홀에 고정합니다.',
    },
    {
      image: '/images/products/traffic-safety/smart-warning-step2.webp',
      text: '2. 점멸등 전원 버튼을 작동해 LED 점멸을 확인합니다.',
    },
    {
      image: '/images/products/traffic-safety/smart-warning-step3.webp',
      text: '3. 경광봉 전원 버튼을 작동해 정상 동작을 확인합니다.',
    },
    {
      image: '/images/products/traffic-safety/smart-warning-step4.webp',
      text: '4. 라바콘에 의한 충격을 감지한 점멸등이 즉시 반응하여, \n 작업자에게 실시간으로 위험 상황을 경고합니다.',
    },
  ];

  return (
    <>
      <HeroSection
        title={`반디별`}
        subtitle={`스마트 경고 시스템으로 \n도로 작업자의 사고를 예방합니다`}
        backgroundImage="/images/products/traffic-safety/smart-warning-using.webp"
        breadcrumbs={[
          { label: '홈', path: '/' },
          { label: '도로 안전경보시스템', path: '/products/traffic-safety' },
          { label: '반디별' },
        ]}
        categories={[
          { label: '도로 안전경보시스템', path: '/products/traffic-safety' },
          { label: '도로 안전유도시설', path: '/products/guardrail' },
          { label: '도로환경 개선시설', path: '/products/facilities' },
          { label: '도로 안내표지시스템', path: '/products/signage' },
        ]}
      />

      <ProductShowcaseSection
        subtitle={`접이식 LED 안전경고시스템`}
        title={`반디별`}
        description={
          <>
            접이식 LED 안전경고시스템 '반디별'은 <strong>고속도로 공사, 긴급 복구, 교통 통제 등 돌발적인 위험 상황에서 {' '}
            운전자에게 신속하고 명확한 정보를 전달하여 2차 사고와 교통 체증을 효과적으로 예방하는 안전 솔루션</strong>입니다.
            <br />
            <br />
            적색·청색 LED가 <strong>고휘도 교차 점멸</strong>하며 후방 차량에 강력한 경고 신호를 제공하여, {' '}
            운전자가 조기에 위험을 인지하고 감속 또는 차로 변경을 할 수 있도록 유도합니다.
            <br />
            <br />
            사용하지 않을 때는 <strong>접이식 구조로 컴팩트하게 보관 및 이동</strong>이 가능해, {' '}
            현장 대응력 향상과 작업자의 안전 확보를 동시에 실현하는 필수 장비입니다.
          </>
        }
        videoSrc="/videos/smart-warning.mp4"
        badgeImages={['/images/logo/Police.webp']}
        
      />

      <SectionDivider />

      <InfoSection title="제품 구성도">
        <TypeBlock groups={compositionGroups} />
      </InfoSection>

      <InfoSection title="사용방법">
        <UsageSteps steps={steps} />
      </InfoSection>

      <InfoSection title="제품 특징">
        <FeatureList features={features} />
      </InfoSection>

      <ProductDetailNav
        category="traffic-safety"
        currentPath="/products/traffic-safety/smart-warning"
        listPath="/products/traffic-safety"
      />

      <CTASection />
    </>
  );
};

export default SmartWarning;
